import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Globe, Package, TrendingUp, ArrowRight, MapPin, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { globalRegions, WHATSAPP_DEFAULT } from '../mock';

const ExportMarkets = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
        <title>Global Export Markets | Worldwide Agricultural Exports – Agro Corp India</title>
        <meta name="description" content="Agro Corp India exports premium agricultural products to international markets worldwide. Basmati rice, jaggery, dehydrated products for global B2B buyers." />
        <meta name="keywords" content="global export markets India, worldwide agricultural export, international food export, Indian agro products global, B2B export destinations" />
        <link rel="canonical" href="https://www.agrocorpindia.com/export-markets" />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative h-[50vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://images.unsplash.com/photo-1701135422723-2bbad52e2e3c')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Global Export Reach
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Delivering premium Indian agro products to international destinations worldwide
          </p>
        </div>
      </section>

      {/* World Map Visualization */}
      <section className="py-20 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider bg-amber-50 px-4 py-2 rounded-full inline-block mb-4">
              Worldwide Export
            </span>
            <h2 className="text-4xl font-bold text-green-900 mb-4">
              International Destinations
            </h2>
            <p className="text-lg text-gray-600">
              Expanding our reach to buyers and distributors across the globe
            </p>
          </div>

          {/* Global Map */}
          <div className="relative bg-white p-8 rounded-2xl shadow-xl mb-12">
            <div className="aspect-video w-full relative bg-gradient-to-br from-blue-50 to-green-50 rounded-xl overflow-hidden">
              <svg viewBox="0 0 1000 500" className="w-full h-full">
                <g opacity="0.3" fill="#059669">
                  <ellipse cx="500" cy="150" rx="80" ry="50" />
                  <ellipse cx="650" cy="200" rx="120" ry="80" />
                  <ellipse cx="250" cy="180" rx="100" ry="70" />
                  <ellipse cx="500" cy="300" rx="70" ry="90" />
                  <ellipse cx="850" cy="350" rx="60" ry="50" />
                </g>

                {/* Global market markers */}
                <g>
                  <circle cx="560" cy="230" r="12" fill="#f59e0b" className="animate-pulse" />
                  <circle cx="560" cy="230" r="22" fill="#f59e0b" opacity="0.2" />
                  <text x="560" y="260" textAnchor="middle" fill="#059669" fontSize="11" fontWeight="bold">Middle East</text>
                </g>
                <g>
                  <circle cx="480" cy="140" r="12" fill="#f59e0b" className="animate-pulse" />
                  <circle cx="480" cy="140" r="22" fill="#f59e0b" opacity="0.2" />
                  <text x="480" y="120" textAnchor="middle" fill="#059669" fontSize="11" fontWeight="bold">Europe</text>
                </g>
                <g>
                  <circle cx="220" cy="160" r="12" fill="#f59e0b" className="animate-pulse" />
                  <circle cx="220" cy="160" r="22" fill="#f59e0b" opacity="0.2" />
                  <text x="220" y="140" textAnchor="middle" fill="#059669" fontSize="11" fontWeight="bold">Americas</text>
                </g>
                <g>
                  <circle cx="780" cy="280" r="12" fill="#f59e0b" className="animate-pulse" />
                  <circle cx="780" cy="280" r="22" fill="#f59e0b" opacity="0.2" />
                  <text x="780" y="310" textAnchor="middle" fill="#059669" fontSize="11" fontWeight="bold">Asia Pacific</text>
                </g>

                {/* India (origin) */}
                <g>
                  <circle cx="640" cy="250" r="14" fill="#16a34a" />
                  <circle cx="640" cy="250" r="24" fill="#16a34a" opacity="0.2" />
                  <text x="640" y="280" textAnchor="middle" fill="#16a34a" fontSize="12" fontWeight="bold">India (HQ)</text>
                </g>

                {/* Connection lines */}
                <line x1="640" y1="250" x2="560" y2="230" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5,5" opacity="0.5" />
                <line x1="640" y1="250" x2="480" y2="140" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5,5" opacity="0.5" />
                <line x1="640" y1="250" x2="220" y2="160" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5,5" opacity="0.5" />
                <line x1="640" y1="250" x2="780" y2="280" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5,5" opacity="0.5" />
              </svg>
            </div>
            <div className="mt-6 flex items-center justify-center space-x-8 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-600 rounded-full"></div>
                <span className="text-gray-600">Origin (India)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-amber-500 rounded-full"></div>
                <span className="text-gray-600">International Destinations</span>
              </div>
            </div>
          </div>

          {/* Region Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {globalRegions.map((region) => (
              <Card key={region.id} className="hover:shadow-2xl transition-all duration-300 border-2 hover:border-green-200 group">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Globe className="w-8 h-8 text-green-700" />
                  </div>
                  <h3 className="text-xl font-bold text-green-900 mb-3">{region.region}</h3>
                  <p className="text-gray-600 text-sm mb-4">{region.description}</p>
                  <div className="space-y-1">
                    <p className="font-semibold text-gray-700 text-sm mb-2">Key Products:</p>
                    {region.products.map((product, idx) => (
                      <p key={idx} className="text-gray-600 text-sm flex items-center">
                        <Package className="w-3 h-3 text-green-600 mr-2 flex-shrink-0" />
                        {product}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <Card className="text-center p-8 border-2 border-green-200">
              <Globe className="w-12 h-12 text-green-700 mx-auto mb-4" />
              <p className="text-4xl font-bold text-green-900 mb-2">4+</p>
              <p className="text-gray-600">Global Regions</p>
            </Card>
            <Card className="text-center p-8 border-2 border-amber-200">
              <Package className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <p className="text-4xl font-bold text-green-900 mb-2">9</p>
              <p className="text-gray-600">Premium Products</p>
            </Card>
            <Card className="text-center p-8 border-2 border-green-200">
              <TrendingUp className="w-12 h-12 text-green-700 mx-auto mb-4" />
              <p className="text-4xl font-bold text-green-900 mb-2">2025</p>
              <p className="text-gray-600">Year Established</p>
            </Card>
            <Card className="text-center p-8 border-2 border-amber-200">
              <MapPin className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <p className="text-4xl font-bold text-green-900 mb-2">100%</p>
              <p className="text-gray-600">Quality Focus</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Expansion */}
      <section className="py-20 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-green-900 mb-6">
            Expanding Our Global Footprint
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            As a growing exporter established in 2025, we are actively building partnerships and bringing
            India's finest agricultural products to discerning buyers worldwide. We are continuously expanding
            to new international destinations.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-green-700 to-green-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Interested in Importing from India?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can meet your requirements with our premium products
          </p>
          <a href={WHATSAPP_DEFAULT} target="_blank" rel="noopener noreferrer">
            <Button className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
              <MessageCircle className="mr-2 w-5 h-5" />
              Inquire on WhatsApp
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ExportMarkets;
